import axios from "axios";
import { config } from "@/appconfig";
import Vorfallmanagement from "./Vorfallmanagement";
import { KorrespondenzArt } from "@/models/Beschwerdemanagement/korrespondenzart-model";
import VorfallmanagementMapper from "./mapper-vfmt";

const controller = "Vorfallmanagement";
const leitlinienController = "LeitlinienEinstellung";

class VorfallmangementApi {
  get url() {
    return `${config.apiUrl}/${controller}`;
  }

  get leitlinienUrl() {
    return `${config.apiUrl}/${leitlinienController}`;
  }

  public async fetchToyFetch(
    urlAddition: string,
    replaceUrl: boolean,
    method: keyof Pick<typeof axios, "get" | "post" | "put" | "patch" | "options" | "head">,
    headers: Request["headers"],
    body?: any
  ): Promise<any> {
    const url = replaceUrl ? urlAddition : `${this.url}${urlAddition}`;
    const mergedHeaders = { ...config.apiHeaders, ...headers };
    const chosenMethod =
      method in axios
        ? method === "get"
          ? axios.get(url, mergedHeaders)
          : method === "patch"
          ? axios.patch(url, body, mergedHeaders)
          : method === "head"
          ? axios.head(url, mergedHeaders)
          : method === "options"
          ? axios.options(url, mergedHeaders)
          : method === "post"
          ? axios.post(url, body, mergedHeaders)
          : method === "put"
          ? axios.put(url, body, mergedHeaders)
          : null
        : null;

    if (chosenMethod) {
      const response = await chosenMethod;
      return response;
    } else throw new Error("could not find matching method " + method);
  }

  /**
   * @description Gibt alle Vorfälle zurück
   */
  public async GetVorfaelle() {
    try {
      const response = await axios.get<Vorfallmanagement.Vorfall.Info[]>(`${this.url}`, config.apiHeaders);
      return VorfallmanagementMapper.mapDatums(response.data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt einen Vorfall zurück
   */
  public async GetVorfall(id: Vorfallmanagement.Vorfall["Id"]) {
    try {
      const response = await axios.get<Vorfallmanagement.Vorfall>(`${this.url}/${id}`, config.apiHeaders);
      return VorfallmanagementMapper.mapVorfall(response.data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Erstellt einen Vorfall
   */
  public async VorfallErstellen(vorfall: Vorfallmanagement.Vorfall.Neu) {
    try {
      const response = await axios.post<Vorfallmanagement.Vorfall>(`${this.url}`, vorfall, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Löschen eines Vorfalls
   */
  public async VorfallLoeschen(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.delete<void>(`${this.url}/${vorfall.Id}`, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Speichern des Status
   */
  public async StatusSpeichern(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.put<Vorfallmanagement.Vorfall>(
        `${this.url}/${vorfall.Id}/status/${vorfall.Status}`,
        vorfall,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Speichern der Erfassung
   */
  public async ErfassungSpeichern(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.put<Vorfallmanagement.Vorfall>(
        `${this.url}/${vorfall.Id}/erfassung`,
        vorfall,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Speichern der Rechtlichen Beurteilung
   */
  public async RechtlicheBeurteilungSpeichern(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.put<Vorfallmanagement.Vorfall>(
        `${this.url}/${vorfall.Id}/rechtlichebeurteilung`,
        vorfall,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Speichern der jeweiligen Aufgabe
   */
  public async AufgabeSpeichern<T>(vorfall: Vorfallmanagement.Vorfall, aufgabe: Vorfallmanagement.Aufgabe<T>) {
    try {
      const response = await axios.post<Vorfallmanagement.Vorfall>(
        `${this.url}/${vorfall.Id}/aufgabe/${aufgabe.Art}`,
        aufgabe.AufgabeDaten,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt die Leitlinien aus den Einstellungen zurück
   */
  public async GetLeitlinien() {
    try {
      const response = await axios.get<Vorfallmanagement.Leitlinie[]>(`${this.leitlinienUrl}/`, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Erstellt eine neue Leitlinie aus den Daten
   */
  public async AddLeitlinie(neu: Vorfallmanagement.Leitlinie.Neu) {
    try {
      const response = await axios.post<Vorfallmanagement.Leitlinie>(`${this.leitlinienUrl}/`, neu, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt die passende Leitlinie zu der Id aus den Einstellungen zurück
   */
  public async GetLeitlinie(id: Vorfallmanagement.Leitlinie["Id"]) {
    try {
      const response = await axios.get<Vorfallmanagement.Leitlinie>(`${this.leitlinienUrl}/${id}`, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt die passende Leitlinie zu der Id aus den Einstellungen zurück
   */
  public async UpdateLeitlinie(leitlinie: Vorfallmanagement.Leitlinie) {
    try {
      const response = await axios.put<Vorfallmanagement.Leitlinie>(
        `${this.leitlinienUrl}/${leitlinie.Id}`,
        leitlinie,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt die passende Leitlinie zu der Id aus den Einstellungen zurück
   */
  public async DeleteLeitlinie(id: Vorfallmanagement.Leitlinie["Id"]) {
    try {
      const response = await axios.delete<boolean>(`${this.leitlinienUrl}/${id}`, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt die möglichen Dokumentenarten zurück
   */
  public async GetDokumenteArten(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const respone = await axios.get<KorrespondenzArt[]>(
        `${this.url}/${vorfall.Id}/korrespondenzartenbyanfrage`,
        config.apiHeaders
      );
      return respone.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Gibt alle Auswahllisten zurück
   */
  public async GetListen() {
    try {
      const response = await axios.get<Vorfallmanagement.Listen>(`${this.url}/listen`, config.apiHeaders);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Speichert eine Korrespondenz
   */
  public async KorrespondenzSenden(
    id: Vorfallmanagement.Vorfall["Id"],
    korrespondenz: Vorfallmanagement.Korrespondenz
  ) {
    try {
      const response = await axios.post<Vorfallmanagement.Vorfall>(
        `${this.url}/${id}/korrespondenz`,
        korrespondenz,
        config.apiHeaders
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Downloaded einen Anhang
   */
  public async GetAnhang(id: Vorfallmanagement.Korrespondenz["Anhaenge"][0]["Guid"]) {
    try {
      const response = await axios.get<File>(`${this.url}/getkorrespondenzanhang/${id}`, {
        ...config.apiHeaders,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Löscht einen Anhang
   */
  public async AnhangLoeschen(
    id: Vorfallmanagement.Korrespondenz["Anhaenge"][0]["Guid"] | Vorfallmanagement.Korrespondenz["Anhaenge"][0]["Id"]
  ) {
    try {
      const response = await axios.delete<void>(`${this.url}/deleteanhang/${id}`, config.apiHeaders);
      return;
    } catch (error) {
      throw error;
    }
  }
    /**
     * @description Fügt Nachmeldung hinzu
     */
  public async Nachmeldung(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.post<void>(`${this.url}/${vorfall.Id}/Nachmeldung`, undefined, config.apiHeaders);
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Löscht die letzte offene Nachmeldung
   */
  public async NachmeldungLoeschen({ Id }: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.delete<void>(`${this.url}/${Id}/Nachmeldung`, config.apiHeaders);
      return;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Fügt Behördliches Schreiben hinzu
   */
  public async BehoerdlichesSchreiben(vorfall: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.post<void>(
        `${this.url}/${vorfall.Id}/BehoerdlichesSchreiben`,
        undefined,
        config.apiHeaders
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Löscht das letzte offene Behördliche Schreiben
   */
  public async BehoerdlichesSchreibenLoeschen({ Id }: Vorfallmanagement.Vorfall) {
    try {
      const response = await axios.delete<void>(`${this.url}/${Id}/BehoerdlichesSchreiben`, config.apiHeaders);
      return;
    } catch (error) {
      throw error;
    }
  }
}

export default VorfallmangementApi;
